import React, { useEffect, useRef, useMemo } from "react";
import {
  Row,
  Col,
  Card,
  Empty,
  Spin,
  Alert,
  message,
  Button,
} from "antd";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import useLiveLocation from "../../../../hooks/useLiveLocation";
import { calculateDistanceMeters } from "../../../../lib/functions";

import {
  fetchActiveDeliverySession,
  arriveDeliveryRoutePoint,
  updateDriverLocation,
  completeDeliveryRoutePoint,
  fetchMyDeliveryRoute,
  startDeliveryRouteSession,
} from "../../deliveryThunks";

import {
  selectActiveDeliverySession,
  selectDeliveryRouteProgress,
  selectCurrentDeliveryPoint,
  selectExecutionRoutePoints,
  selectDeliveryExecutionLoading,
  selectMyDeliveryRoute,
  selectMyDeliveryRouteLoading,
} from "../../deliverySelectors";

import RouteHeader from "./components/RouteHeader";
import ProgressCard from "./components/ProgressCard";
import CurrentStopCard from "./components/CurrentStopCard";
import StopTimeline from "./components/StopTimeline";
import DeliveryMap from "./components/DeliveryMap";
import CompletionModal from "./components/CompletionModal";

const DeliveryExecution = ({center,zoom}) => {

  const LOCATION_SYNC_INTERVAL = 15000; //15ec
  const MIN_MOVEMENT_DISTANCE = 10; //10m

  const [completionOpen, setCompletionOpen] = useState(false);
  const [completionLoading, setCompletionLoading] = useState(false);

  const dispatch = useDispatch();
  const session = useSelector(
    selectActiveDeliverySession
  );
  const progress = useSelector(
    selectDeliveryRouteProgress
  );
  const currentPoint = useSelector(
    selectCurrentDeliveryPoint
  );
  const routePoints = useSelector(
    selectExecutionRoutePoints
  );
  // const driverLocation = useSelector(
  //   selectDriverLocation
  // );
  const loading = useSelector(
    selectDeliveryExecutionLoading
  );

  const {
      location,
      loading: gpsLoading,
      error: gpsError,
  } = useLiveLocation();

  const lastSyncTimeRef = useRef(0);
  const lastSyncedLocationRef = useRef(null);

  const myRoute = useSelector(selectMyDeliveryRoute);
  const myRouteLoading = useSelector(selectMyDeliveryRouteLoading);
  

  useEffect(() => {
    dispatch(fetchMyDeliveryRoute());
    dispatch(fetchActiveDeliverySession());
  }, [dispatch]);  

  const distance = useMemo(() => {
      if (
          !location ||
          !currentPoint ||
          !currentPoint.latitude ||
          !currentPoint.longitude
      )
      return null;
      return calculateDistanceMeters(
          Number(location.latitude),
          Number(location.longitude),
          Number(currentPoint.latitude),
          Number(currentPoint.longitude)
      );
  }, [location, currentPoint]);

  const insideRadius = useMemo(() => {
      if (distance == null || !currentPoint)
          return false;
      return distance <= Number(currentPoint.radius_meters || 100);
  }, [distance, currentPoint]);

  const driverLocation = useMemo(() => {
      if (!location) return null;
      return {
          lat: Number(location.latitude),
          lng: Number(location.longitude),
      };
  }, [location]);
  
  useEffect(() => {
    if (!location || !session?.id) return;
    
    const now = Date.now();
    if (now - lastSyncTimeRef.current < LOCATION_SYNC_INTERVAL) {
      return;
    }
    
    if (lastSyncedLocationRef.current) {
      const moved = calculateDistanceMeters(
        lastSyncedLocationRef.current.latitude,
        lastSyncedLocationRef.current.longitude,
        location.latitude,
        location.longitude,
      );

      if (moved < MIN_MOVEMENT_DISTANCE) {
        return;
      }
    }

    lastSyncTimeRef.current = now;
    lastSyncedLocationRef.current = location;
    dispatch(
      updateDriverLocation({
        session_id: session.id,
        latitude: location.latitude,
        longitude: location.longitude,
        accuracy: location.accuracy,
        speed: location.speed,
        heading: location.heading,
      }),
    );
  }, [location, session?.id, dispatch]);

  const handleNavigate = (point) => {
    if (!point?.latitude || !point?.longitude)
      return;
    window.open(
      `https://www.google.com/maps/dir/?api=1&destination=${point.latitude},${point.longitude}`,
      "_blank"
    );

  };

  const handleArrive = () => {

    if (!session || !currentPoint)
      return;

    if (!location) {
        message.warning(
            "Waiting for GPS location..."
        );
        return;
    }

    if (!insideRadius) {
      message.warning(
        "You must be inside the delivery radius."
      );
      return;
    }

    dispatch(
      arriveDeliveryRoutePoint({
        session_id: session.id,
        route_point_id:
          currentPoint.route_point_id,
      })
    );

  };

  const handleCompleteDelivery = async (
    values
  ) => {
    try {
      setCompletionLoading(true);

      await dispatch(
        completeDeliveryRoutePoint({
          session_id: session.id,
          route_point_id:
            currentPoint.route_point_id,
          recipient_name:
            values.recipient_name,
          recipient_phone:
            values.recipient_phone,
          remarks: values.remarks,
        })
      );

      setCompletionOpen(false);

      dispatch(fetchActiveDeliverySession());      

      message.success(
        "Delivery completed successfully."
      );
    } finally {
      setCompletionLoading(false);
    }
  };

  if (loading) {

    return (

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          padding: 80,
        }}
      >
        <Spin size="large" />
      </div>

    );

  }

  if (!session) {
  if (myRouteLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          padding: 80,
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (!myRoute) {
    return (
      <Card>
        <Empty description="No delivery route assigned for today." />
      </Card>
    );
  }

  return (
    <Card>
      <RouteHeader
        session={{
          route_name: myRoute.route_name,
          description: myRoute.description,
        }}
      />

      <div style={{ marginTop: 16 }}>
        <Alert
          type="info"
          showIcon
          message="Delivery route assigned"
          description={`You have been assigned ${myRoute.route_name} for today.`}
        />

        <Button
          type="primary"
          style={{ marginTop: 16 }}
          loading={loading}
          onClick={async () => {
            await dispatch(
              startDeliveryRouteSession({
                assignment_id: myRoute.id,
              })
            );
          }}
        >
          Start Route
        </Button>
      </div>
    </Card>
  );
}

  return (

    <div
      style={{
        padding: 16,
      }}
    >

      {gpsError && (
          <Alert
              type="warning"
              showIcon
              message="GPS unavailable"
              description={gpsError}
              style={{
                  marginBottom:16
              }}
          />
      )}

      <Alert
        type="success"
        message="Delivery Route In Progress"
        style={{
          marginBottom: 16,
        }}
      />

      <Row gutter={16}>

        <Col
          xs={24}
          lg={9}
        >

          <RouteHeader
            session={session}
          />

          <ProgressCard
            progress={progress}
          />

          <CurrentStopCard
            point={currentPoint}
            distance={distance}
            insideRadius={insideRadius}
            status={currentPoint?.visit_status}
            loading={loading}
            onNavigate={handleNavigate}
            onArrive={handleArrive}
            onComplete={() =>
                setCompletionOpen(true)
            }
          />

          <StopTimeline
            points={routePoints}
          />

        </Col>

        <Col
          xs={24}
          lg={15}
        >

          <DeliveryMap
            session={session}
            points={routePoints}
            currentPoint={currentPoint}
            driverLocation={driverLocation}
          />

        </Col>

      </Row>

      <CompletionModal
        showRecipientPhone={true}
        showPhoto={false}
        showSignature={false}
        showOTP={false}
        maskClosable={false}
        open={completionOpen}
        point={currentPoint}
        loading={completionLoading}
        onCancel={() =>
          setCompletionOpen(false)
        }
        onSubmit={handleCompleteDelivery}
      />

    </div>

  );

};

export default DeliveryExecution;